import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import type { ExchangeAccount, Exchange } from '../types';
import { exchangeApi } from '../services/api';
import { copyTradeApi } from '../services/copyTradeApi';
import { useTranslation } from '../hooks/useTranslation';
import { showTelegramAlert } from '../services/telegramService';
import { useModal } from '../hooks/useModal';
import CustomModal from '../components/CustomModal';
import ConnectExchange from '../components/exchanges/ConnectExchange';
import PageSkeleton from '../components/skeletons/PageSkeleton';
import { useTelegramTheme } from '../hooks/useTelegramTheme';

interface CopyTradeAccount {
  id: string;
  exchange: string;
  uid: string;
  status: 'pending' | 'active' | 'rejected';
  disabled?: boolean;
  totalProfit: number;
  totalVolume: number;
  totalRebate: number;
  createdAt: string;
  approvedAt?: string;
  copyTradeImage?: string;
  botId?: string | null;
  bot?: {
    id: string;
    name: string;
    nameVi: string;
    image: string;
  } | null;
}

const Exchanges = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTelegramTheme();
  const { modal, closeModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<ExchangeAccount[]>([]);
  const [copyTradeAccounts, setCopyTradeAccounts] = useState<CopyTradeAccount[]>([]);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'rejected'>('all');
  const [exchangeFilter, setExchangeFilter] = useState<string>('all');
  const [expandedSections, setExpandedSections] = useState({
    exchange: false,
    copytrade: false,
  });
  const [rejectionReasonModal, setRejectionReasonModal] = useState<{ isOpen: boolean; accountId: string | null }>({
    isOpen: false,
    accountId: null
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // Check if connect query param is present
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'true') {
      setShowConnect(true);
      // Remove the query param from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load exchanges, exchange accounts, and copy trade accounts in parallel
      const [exchangesResponse, accountsData, copyTradeAccountsData] = await Promise.all([
        exchangeApi.getExchanges(),
        exchangeApi.getAccounts(),
        copyTradeApi.getAccounts()
      ]);

      setExchanges(exchangesResponse.exchanges);
      setAccounts(accountsData);
      setCopyTradeAccounts(copyTradeAccountsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectComplete = async () => {
    setShowConnect(false);
    await loadData();
    // Update user data
    const { updateUser } = useAuthStore.getState();
    const { userApi } = await import('../services/api');
    try {
      const updatedUser = await userApi.getProfile();
      updateUser({
        balance: updatedUser.balance || 0,
        pendingRebate: updatedUser.pendingRebate || 0,
        points: updatedUser.points || 0,
      });
    } catch (error) {
      console.error('Failed to update user:', error);
    }
    showTelegramAlert(t('exchanges.exchangeConnected'));
  };

  const handleViewReason = (accountId: string) => {
    setRejectionReasonModal({ isOpen: true, accountId });
  };

  const handleReconnect = async (accountId: string) => {
    setRejectionReasonModal({ isOpen: false, accountId: null });
    // Find the account to get exchange and UID
    const account = accounts.find(acc => acc.id === accountId);
    if (!account) return;

    // Open ConnectExchange modal with pre-filled data
    setShowConnect(true);
    // Note: You might need to pass the account data to ConnectExchange component
    // For now, user will need to re-enter the UID
  };

  const getRejectionReason = () => {
    // Sửa lại nội dung để tránh bản quyền
    return t('exchanges.rejectionReasonText');
  };

  const getExchangeName = (exchange: string) => {
    return t(`linkExchange.${exchange}`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const filteredAccounts = accounts.filter(account => {
    if (statusFilter !== 'all' && account.status !== statusFilter) return false;
    if (exchangeFilter !== 'all' && account.exchange !== exchangeFilter) return false;
    return true;
  });

  const filteredCopyTradeAccounts = copyTradeAccounts.filter(account => {
    if (statusFilter !== 'all' && account.status !== statusFilter) return false;
    if (exchangeFilter !== 'all' && account.exchange !== exchangeFilter) return false;
    return true;
  });

  if (loading) {
    return <PageSkeleton />;
  }

  if (showConnect) {
    return (
      <ConnectExchange
        onComplete={handleConnectComplete}
        onCancel={() => setShowConnect(false)}
        onBackToList={() => {
          setShowConnect(false);
          loadData();
        }}
      />
    );
  }

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shadow-lg">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="">
          <h1 className="text-2xl font-bold text-white">
            {t('exchanges.title')}
          </h1>
          <p className="text-tg-hint text-sm">
            {t('exchanges.chooseReferralType')}
          </p>
        </div>
      </div>

      {/* Quick Links - Phân biệt 2 loại ref */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => setShowConnect(true)}
          className="relative overflow-hidden p-6 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-600 rounded-2xl text-white transition-all active:scale-95 hover:scale-105 group"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative flex flex-col items-center text-center">
            <div className="w-16 h-16 mb-3 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="font-bold text-base leading-tight mb-1 h-10 flex items-center">
              {t('exchanges.exchangeReferral')}
            </div>
            <div className="text-xs opacity-90 h-8 flex items-center">
              {t('exchanges.linkYourUid')}
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/link-copy-trade')}
          className="relative overflow-hidden p-6 bg-gradient-to-br from-orange-600 via-yellow-500 to-orange-600 rounded-2xl text-white transition-all active:scale-95 hover:scale-105 group"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative flex flex-col items-center text-center">
            <div className="w-16 h-16 mb-3 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div className="font-bold text-base leading-tight mb-1 h-10 flex items-center">
              {t('exchanges.copyTrade')}
            </div>
            <div className="text-xs opacity-90 h-8 flex items-center">
              {t('exchanges.followTrades')}
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {[
          { key: 'all', label: t('exchanges.allAccounts') },
          { key: 'active', label: t('exchanges.activeAccounts') },
          { key: 'pending', label: t('exchanges.pendingAccounts') },
          { key: 'rejected', label: t('exchanges.rejectedAccounts') },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key as any)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-300 ${statusFilter === tab.key
              ? 'bg-primary text-black shadow-lg shadow-primary/30'
              : 'bg-tg-secondary text-tg-hint border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:scale-105'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Exchange Filter */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 font-medium">{t('exchanges.filterByExchange')}</span>
          <select
            value={exchangeFilter}
            onChange={(e) => setExchangeFilter(e.target.value)}
            className={`flex-1 px-4 py-2.5 rounded-xl border focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium ${
              theme === 'light'
                ? 'bg-white text-gray-900 border-gray-300'
                : 'bg-tg-secondary text-white border-gray-700/50'
            }`}
          >
            <option value="all" className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-gray-900'}>{t('exchanges.allExchanges')}</option>
            <option value="binance" className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-gray-900'}>{t('linkExchange.binance')}</option>
            <option value="bybit" className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-gray-900'}>{t('linkExchange.bybit')}</option>
            <option value="okx" className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-gray-900'}>{t('linkExchange.okx')}</option>
            <option value="gate" className={theme === 'light' ? 'bg-white text-gray-900' : 'bg-gray-900'}>{t('linkExchange.gate')}</option>
          </select>
        </div>
      </div>

      {/* ========== SECTION 1: EXCHANGE REFERRAL ========== */}
      <div className="mb-6">
        <button
          onClick={() => setExpandedSections(prev => ({ ...prev, exchange: !prev.exchange }))}
          className="w-full flex items-center justify-between mb-3 p-5 bg-gradient-to-r from-blue-600/10 via-blue-500/10 to-blue-600/10 rounded-2xl border border-blue-500/20 hover:border-blue-400/50 hover:shadow-lg hover:shadow-blue-500/20 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/40 group-hover:scale-110 transition-transform">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-base font-bold text-white mb-0.5">
                {t('exchanges.exchangeReferral')}
              </h2>
              <p className="text-xs text-blue-400 font-medium">
                {filteredAccounts.length} {t('exchanges.accounts')}
              </p>
            </div>
          </div>
          <svg
            className={`w-6 h-6 text-blue-400 transition-transform duration-300 ${expandedSections.exchange ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.exchange && (filteredAccounts.length > 0 ? (
          <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
            <div className="flex gap-4">
              {filteredAccounts.map((account) => {
              // Find exchange info for this account (case-insensitive match)
              const exchangeInfo = exchanges.find(ex =>
                ex.code?.toLowerCase() === account.exchange?.toLowerCase()
              );

              // Get cashback info from exchange
              const cashbackInfo = exchangeInfo?.cashbackInfo;
              const exchangeCashback = cashbackInfo?.exchangeCashback ?? 0;
              const hubCashback = cashbackInfo?.hubCashback ?? 0;
              const totalCashback = cashbackInfo?.totalCashback ?? (exchangeCashback + hubCashback);

              // Get real data from account or use 0 as default
              const totalRebate = account.totalRebate || 0; // Already calculated user rebate from backend
              const transactionVolume = account.transactionVolume || 0;
              
              // totalRebate from backend is already the user's portion (after applying %)
              // So we display it directly as CashbackHub Rebate
              const exchangeRebateAmount = 0; // Exchange doesn't pay user directly (only commission to admin)
              const hubRebateAmount = totalRebate; // This is what user gets from CashbackHub

              return (
                <div key={account.id} className="min-w-[320px] w-[85vw] max-w-[400px] flex-shrink-0 bg-tg-secondary rounded-xl p-4 border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 cursor-pointer">
                  {/* Exchange Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-tg-bg rounded-lg flex items-center justify-center overflow-hidden">
                        {exchangeInfo?.icon ? (
                          <img
                            src={exchangeInfo.icon}
                            alt={exchangeInfo.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xl">{account.exchange === 'bybit' ? '🔵' : account.exchange === 'okx' ? '⚪' : '🟡'}</span>
                        )}
                      </div>
                      <div>
                        <div className="text-base font-semibold text-white">
                          {exchangeInfo ? exchangeInfo.name : getExchangeName(account.exchange)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-tg-hint">
                          <span>UID: {account.uid}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(account.uid);
                              showTelegramAlert(t('exchanges.uidCopied'));
                            }}
                            className="text-primary"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${account.status === 'active'
                      ? 'bg-primary/20 text-primary'
                      : account.status === 'pending'
                        ? 'bg-orange-500/20 text-orange-500'
                        : 'bg-red-500/20 text-red-500'
                      }`}>
                      {account.status === 'active'
                        ? t('linkExchange.active')
                        : account.status === 'pending'
                          ? t('rebate.pending')
                          : t('exchanges.rejectedAccounts')}
                    </div>
                  </div>

                  {/* Cashback Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-tg-hint">
                        {cashbackInfo?.exchangeCashbackLabel || getExchangeName(account.exchange)}
                      </span>
                      <span className="text-white">{exchangeCashback.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-tg-hint">
                        {cashbackInfo?.hubCashbackLabel || 'CashbackHub'}
                      </span>
                      <span className="text-primary font-semibold">+{hubCashback.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-800">
                      <span className="text-primary font-semibold">
                        {t('common.all')}:
                      </span>
                      <span className="text-primary font-bold">{totalCashback.toFixed(2)}%</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <div className="text-xs text-tg-hint mb-1">
                        {t('exchanges.totalRebate')}
                      </div>
                      <div className="text-base font-semibold text-white">
                        {formatCurrency(totalRebate)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-tg-hint mb-1">
                        {t('exchanges.transactionVolume')}
                      </div>
                      <div className="text-base font-semibold text-white">
                        {formatCurrency(transactionVolume)}
                      </div>
                      {transactionVolume === 0 && account.status === 'active' && (
                        <div className="text-xs text-orange-400 mt-1">
                          {t('exchanges.waitingForSync')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rebate Breakdown */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-gray-800">
                    <div className="flex justify-between text-sm">
                      <span className="text-tg-hint">
                        {getExchangeName(account.exchange)}
                      </span>
                      <span className="text-white">{formatCurrency(exchangeRebateAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-tg-hint">
                        {t('exchanges.cashbackHubRebate')}
                      </span>
                      <span className="text-primary font-semibold">+{formatCurrency(hubRebateAmount)}</span>
                    </div>
                  </div>

                  {/* Timestamp and Actions */}
                  <div className="flex items-center justify-between text-xs text-tg-hint">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{new Date(account.createdAt).toLocaleString()}</span>
                    </div>
                    {account.status === 'rejected' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewReason(account.id)}
                          className="text-primary hover:text-primary/80 transition-colors font-semibold"
                        >
                          {t('exchanges.viewReason')}
                        </button>
                        <button
                          onClick={() => handleReconnect(account.id)}
                          className="text-primary hover:text-primary/80 transition-colors font-semibold"
                        >
                          {t('exchanges.reconnect')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          <div className="bg-tg-secondary rounded-xl p-8 text-center border border-blue-500/20">
            <div className="w-16 h-16 mx-auto mb-3 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="text-sm text-tg-hint">
              {t('exchanges.noAccounts')}
            </div>
          </div>
        ))}
      </div>

      {/* ========== SECTION 2: COPY TRADE ========== */}
      <div className="mb-6">
        <button
          onClick={() => setExpandedSections(prev => ({ ...prev, copytrade: !prev.copytrade }))}
          className="w-full flex items-center justify-between mb-3 p-5 bg-gradient-to-r from-orange-600/10 via-yellow-500/10 to-orange-600/10 rounded-2xl border border-orange-500/20 hover:border-orange-400/50 hover:shadow-lg hover:shadow-orange-500/20 transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/40 group-hover:scale-110 transition-transform">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div className="text-left">
              <h2 className="text-base font-bold text-white mb-0.5">
                {t('exchanges.copyTrade')}
              </h2>
              <p className="text-xs text-orange-400 font-medium">
                {filteredCopyTradeAccounts.length} {t('exchanges.accounts')}
              </p>
            </div>
          </div>
          <svg
            className={`w-6 h-6 text-orange-400 transition-transform duration-300 ${expandedSections.copytrade ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.copytrade && (
          // Copy Trade Accounts Tab
          filteredCopyTradeAccounts.length > 0 ? (
            <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
              <div className="flex gap-4">
                {filteredCopyTradeAccounts.map((account) => (
                  <div key={account.id} className="min-w-[320px] w-[85vw] max-w-[400px] flex-shrink-0 bg-tg-secondary rounded-xl p-4 border border-gray-800 hover:border-primary/50 hover:bg-[#2a2a2a] hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 cursor-pointer">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Show bot image if available, otherwise show exchange image */}
                      {account.bot?.image ? (
                        <img 
                          src={account.bot.image} 
                          alt={account.bot.name}
                          className="w-12 h-12 rounded-xl object-cover border-2 border-orange-500/30"
                          onError={(e) => {
                            // Fallback to exchange image or gradient icon
                            if (account.copyTradeImage) {
                              (e.target as HTMLImageElement).src = account.copyTradeImage;
                            } else {
                              (e.target as HTMLImageElement).style.display = 'none';
                              const parent = (e.target as HTMLImageElement).parentElement;
                              if (parent) {
                                const div = document.createElement('div');
                                div.className = 'w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center';
                                div.innerHTML = `<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>`;
                                parent.appendChild(div);
                              }
                            }
                          }}
                        />
                      ) : account.copyTradeImage ? (
                        <img 
                          src={account.copyTradeImage} 
                          alt={account.exchange}
                          className="w-12 h-12 rounded-xl object-cover border-2 border-orange-500/30"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              const div = document.createElement('div');
                              div.className = 'w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center';
                              div.innerHTML = `<svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>`;
                              parent.appendChild(div);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center">
                          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                          </svg>
                        </div>
                      )}
                      <div>
                        <div className="text-lg font-bold text-white">
                          {account.bot?.name || account.exchange.toUpperCase()}
                        </div>
                        <div className="text-xs text-orange-300">
                          {account.bot?.nameVi || 'Copy Trade'}
                        </div>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      account.disabled
                        ? 'bg-gray-500/20 text-gray-400'
                        : account.status === 'active'
                        ? 'bg-primary/20 text-primary'
                        : account.status === 'pending'
                        ? 'bg-orange-500/20 text-orange-500'
                        : 'bg-red-500/20 text-red-500'
                      }`}>
                      {account.disabled ? t('copyTrade.disabled') : account.status}
                    </div>
                  </div>

                  {/* UID */}
                  <div className="text-sm mb-3">
                    UID: {account.uid}
                  </div>

                  {/* Stats for active accounts */}
                  {account.status === 'active' && (
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="bg-black/30 rounded-lg p-3 border border-orange-500/20">
                        <div className="text-xs text-tg-hint mb-1">
                          {t('exchanges.totalProfit')}
                        </div>
                        <div className="text-base font-semibold text-primary">
                          {formatCurrency(account.totalProfit)}
                        </div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3 border border-primary/30">
                        <div className="text-xs text-tg-hint mb-1">
                          {t('exchanges.totalRebate') || 'Total Rebate'}
                        </div>
                        <div className="text-base font-semibold text-primary">
                          {formatCurrency(account.totalRebate || 0)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className="flex items-center gap-1 text-xs text-tg-hint">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{new Date(account.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-orange-900/20 to-yellow-900/20 rounded-xl p-8 text-center border border-orange-500/20">
              <div className="w-16 h-16 mx-auto mb-3 bg-orange-500/10 rounded-xl flex items-center justify-center">
                <svg className="w-10 h-10 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div className="text-sm text-orange-200">
                {t('exchanges.noCopyTradeAccounts')}
              </div>
            </div>
          )
        )}
      </div>

      {/* Rejection Reason Modal */}
      <CustomModal
        isOpen={rejectionReasonModal.isOpen}
        onClose={() => setRejectionReasonModal({ isOpen: false, accountId: null })}
        title={t('exchanges.rejectionReason')}
        message={getRejectionReason()}
        type="confirm"
        confirmText={t('exchanges.reconnect')}
        cancelText={t('common.close')}
        onConfirm={() => {
          if (rejectionReasonModal.accountId) {
            handleReconnect(rejectionReasonModal.accountId);
          }
        }}
        onCancel={() => setRejectionReasonModal({ isOpen: false, accountId: null })}
      />

      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default Exchanges;


